import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import Logo from './Logo';
import {
  getSettings, customerLogin, customerRegister, customerForgotPassword,
  getCustomerToken, setCustomerToken, clearCustomerToken, customerMe,
} from '../lib/api';

export const SiteGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | locked | open
  const [mode, setMode] = useState('login'); // login | register
  const [form, setForm] = useState({ fullName: '', email: '', password: '' });
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      // Password-reset links must always reach the reset form, gate or not.
      if (new URLSearchParams(window.location.search).get('reset')) { setStatus('open'); return; }
      try {
        const s = await getSettings();
        if (!s.siteGateEnabled) { setStatus('open'); return; }
        if (getCustomerToken()) {
          try { await customerMe(); setStatus('open'); return; } catch (e) { clearCustomerToken(); }
        }
        setStatus('locked');
      } catch (e) { setStatus('open'); }
    })();
  }, []);

  const setf = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    if (!confirmed) { setError('Please confirm you are 21+ and products are for research use only.'); return; }
    setBusy(true);
    try {
      const res = mode === 'login'
        ? await customerLogin(form.email.trim(), form.password)
        : await customerRegister(form.fullName.trim(), form.email.trim(), form.password);
      setCustomerToken(res.token);
      setStatus('open');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    setError(''); setNotice('');
    if (!form.email.trim()) { setError('Enter your email above first, then tap "Forgot password?".'); return; }
    try {
      await customerForgotPassword(form.email.trim());
      setNotice('If an account exists for that email, a reset link is on its way.');
    } catch (e) { setNotice('If an account exists for that email, a reset link is on its way.'); }
  };

  if (status === 'open') return children;

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-alp-black flex items-center justify-center" data-testid="site-gate-loading">
        <div className="animate-pulse"><Logo size={56} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alp-black relative flex items-center justify-center px-5 py-14 overflow-hidden" data-testid="site-gate">
      {/* Gold ambience */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 65% 45% at 50% -5%, rgba(201,162,75,0.14), transparent 60%), radial-gradient(ellipse 50% 35% at 50% 110%, rgba(201,162,75,0.07), transparent 65%)' }} />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-px h-40 bg-gradient-to-b from-alp-gold/50 to-transparent" />

      <div aria-hidden className="gold-orb w-64 h-64 -right-10 top-1/4" />
      <div className="relative w-full max-w-md text-center">
        <div className="flex justify-center mb-6"><Logo size={72} /></div>
        <p className="text-alp-gold text-[11px] tracking-[0.35em] uppercase mb-2">Private Client Access</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-alp-cream heading-glow mb-4">Australian Luxe</h1>
        <p className="text-alp-gray text-sm leading-relaxed mb-4">
          In accordance with industry regulations, please sign in or create an account to browse our website and view product details.
        </p>
        <p className="text-alp-cream/70 text-xs leading-relaxed mb-8 border border-alp-gold/25 rounded-lg bg-alp-gold/[0.05] px-4 py-3" data-testid="site-gate-returning-note">
          <span className="text-alp-gold">Ordered with us before?</span> Your existing account works here &mdash; just sign in. New account? Register with the same email you used at checkout and your previous orders will appear in your account automatically.
        </p>

        {/* Login / Register tabs */}
        <div className="flex justify-center gap-8 mb-7">
          {['login', 'register'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => { setMode(m); setError(''); setNotice(''); }}
              data-testid={`site-gate-tab-${m}`}
              className={`pb-2 text-sm tracking-[0.2em] uppercase transition-colors border-b-2 ${mode === m ? 'text-alp-gold border-alp-gold' : 'text-alp-gray border-transparent hover:text-alp-cream'}`}
            >
              {m === 'login' ? 'Sign In' : 'Register'}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="text-left space-y-4">
          {mode === 'register' && (
            <input
              type="text" value={form.fullName} onChange={(e) => setf('fullName', e.target.value)}
              placeholder="Full name" data-testid="site-gate-name-input" autoComplete="name"
              className="w-full bg-alp-card/50 border border-alp-line rounded px-4 py-3.5 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
            />
          )}
          <input
            type="email" value={form.email} onChange={(e) => setf('email', e.target.value)}
            placeholder="Email address" data-testid="site-gate-email-input" autoComplete="email" autoFocus
            className="w-full bg-alp-card/50 border border-alp-line rounded px-4 py-3.5 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
          />
          <input
            type="password" value={form.password} onChange={(e) => setf('password', e.target.value)}
            placeholder={mode === 'register' ? 'Create a password (6+ characters)' : 'Password'}
            data-testid="site-gate-password-input" autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            className="w-full bg-alp-card/50 border border-alp-line rounded px-4 py-3.5 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
          />

          <label className="flex items-start gap-3 cursor-pointer pt-1 select-none">
            <input
              type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="site-gate-confirm-checkbox" className="mt-0.5 accent-alp-gold"
            />
            <span className="text-alp-cream/75 text-xs leading-relaxed">I confirm I am 21+ and understand all products are for research use only. Not for human consumption.</span>
          </label>

          {error && <p className="text-red-400 text-sm" data-testid="site-gate-error">{error}</p>}
          {notice && <p className="text-alp-gold text-sm" data-testid="site-gate-notice">{notice}</p>}

          <button
            type="submit" disabled={busy || !form.email || !form.password || (mode === 'register' && !form.fullName)}
            data-testid="site-gate-submit"
            className="btn-glow w-full inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black py-3.5 text-xs tracking-[0.25em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50"
          >
            <Lock size={14} /> {busy ? 'One moment…' : mode === 'login' ? 'Enter the Boutique' : 'Create Account & Enter'}
          </button>
        </form>

        {mode === 'login' && (
          <button type="button" onClick={forgot} data-testid="site-gate-forgot-link" className="mt-4 text-alp-gray hover:text-alp-gold text-[11px] tracking-[0.1em] uppercase underline underline-offset-4 transition-colors">
            Forgot password?
          </button>
        )}

        <p className="text-alp-gray/60 text-[11px] leading-relaxed mt-10">
          Australian Luxe Peptides &middot; Research use only &middot; Australia only
        </p>
      </div>
    </div>
  );
};
